import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, AlertTriangle } from 'lucide-react';

/**
 * StrengthEntryEditModal — quick-edit + delete for a strength workout entry
 * shown on the Progress page "Recent workouts" grid. Mirrors the
 * Edit/Delete affordance Endurance already has on ActivityDetail.
 *
 * Editable fields (whitelisted server-side):
 *   - title, date (YYYY-MM-DD picker), duration (min), totalVolume (lb),
 *     exercisesCompleted (out of exerciseCount).
 *   - notes (free text, optional).
 *
 * Save  → calls onSave(patch) — parent fires updateWorkout(entry.id, patch).
 * Delete → confirm-step, then onDelete() — parent fires removeWorkout(entry.id).
 */
const isoDateOnly = (iso) => {
  // Returns YYYY-MM-DD in the user's local tz. Inputs are ISO timestamps that
  // were originally saved at the user's local end-of-day, so taking the local
  // date back is round-trip safe.
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch { return ''; }
};

const localDateToIso = (yyyymmdd) => {
  // Convert YYYY-MM-DD back to a full ISO timestamp anchored at 23:59 local —
  // matches Strength.jsx's logging convention so chart binning lands on the
  // intended calendar day.
  if (!yyyymmdd) return null;
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  if (!y || !m || !d) return null;
  const local = new Date(y, m - 1, d, 23, 59, 0, 0);
  return local.toISOString();
};

const StrengthEntryEditModal = ({ entry, open, onClose, onSave, onDelete }) => {
  const [draft, setDraft] = useState({
    title: '',
    dateLocal: '',
    duration: '',
    totalVolume: '',
    exercisesCompleted: '',
    notes: '',
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open || !entry) return;
    setDraft({
      title: entry.title || '',
      dateLocal: isoDateOnly(entry.date),
      duration: entry.duration ?? '',
      totalVolume: entry.totalVolume ?? '',
      exercisesCompleted: entry.exercisesCompleted ?? '',
      notes: entry.notes || '',
    });
    setConfirmDelete(false);
  }, [open, entry]);

  if (!open || !entry) return null;

  const handleSave = () => {
    const patch = {};
    if (draft.title !== entry.title) patch.title = draft.title;
    const newIso = localDateToIso(draft.dateLocal);
    if (newIso && newIso !== entry.date) patch.date = newIso;
    const newDuration = draft.duration === '' ? null : Number(draft.duration);
    if (newDuration !== null && newDuration !== entry.duration) patch.duration = newDuration;
    const newVolume = draft.totalVolume === '' ? null : Number(draft.totalVolume);
    if (newVolume !== null && newVolume !== entry.totalVolume) patch.totalVolume = newVolume;
    const newCompleted = draft.exercisesCompleted === '' ? null : Number(draft.exercisesCompleted);
    if (newCompleted !== null && newCompleted !== entry.exercisesCompleted) patch.exercisesCompleted = newCompleted;
    if ((draft.notes || '') !== (entry.notes || '')) patch.notes = draft.notes;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    onSave(patch);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="strength-edit-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-1">Edit workout</div>
            <div className="font-display text-3xl tracking-wide leading-none">{entry.title}</div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-enf-surface-mid text-enf-text-muted hover:text-enf-text-primary transition-colors"
            data-testid="strength-edit-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Title">
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
              data-testid="strength-edit-title"
            />
          </Field>
          <Field label="Date">
            <input
              type="date"
              value={draft.dateLocal}
              onChange={(e) => setDraft((d) => ({ ...d, dateLocal: e.target.value }))}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
              data-testid="strength-edit-date"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Duration (min)">
              <input
                type="number" inputMode="numeric" min="0"
                value={draft.duration}
                onChange={(e) => setDraft((d) => ({ ...d, duration: e.target.value }))}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="strength-edit-duration"
              />
            </Field>
            <Field label="Volume (lb)">
              <input
                type="number" inputMode="numeric" min="0"
                value={draft.totalVolume}
                onChange={(e) => setDraft((d) => ({ ...d, totalVolume: e.target.value }))}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="strength-edit-volume"
              />
            </Field>
            <Field label={`Done / ${entry.exerciseCount ?? '?'}`}>
              <input
                type="number" inputMode="numeric" min="0" max={entry.exerciseCount ?? undefined}
                value={draft.exercisesCompleted}
                onChange={(e) => setDraft((d) => ({ ...d, exercisesCompleted: e.target.value }))}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="strength-edit-completed"
              />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg resize-none"
              data-testid="strength-edit-notes"
            />
          </Field>
        </div>

        <div className="mt-6 pt-5 border-t border-enf-surface-border flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-enf-role-danger hover:bg-enf-role-danger/10 rounded-md transition-colors"
              data-testid="strength-edit-delete-trigger"
            >
              <Trash2 className="w-4 h-4" /> Delete workout
            </button>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-enf-role-warning" />
              <span className="text-enf-text-secondary">Sure?</span>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-2 py-1 rounded-md text-enf-text-muted hover:text-enf-text-primary"
                data-testid="strength-edit-delete-cancel"
              >Cancel</button>
              <button
                onClick={handleDelete}
                className="px-3 py-1 rounded-md bg-enf-role-danger text-white font-semibold hover:bg-red-600 transition-colors"
                data-testid="strength-edit-delete-confirm"
              >Delete</button>
            </div>
          )}

          <div className="flex gap-2 sm:ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm"
              data-testid="strength-edit-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md font-semibold text-sm transition-colors"
              data-testid="strength-edit-save"
            >
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">{label}</span>
    {children}
  </label>
);

export default StrengthEntryEditModal;
